import React, { useMemo } from 'react';
import keyBy from 'lodash/keyBy';

import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import { EMRoutingContextInterface } from '@om-fe/core/core/routing/utils';
import { DisciplineLiveStatus, MarketGroupOverviewModel, DisciplinesType } from '@om-fe/models';
import { MarketsHeaderOptions } from '@om-fe/core/utils/config';
import { PlatformTypes } from '@om-fe/utils';
import { GetMarketGroupsResponse, GetMarketGroupsOptions } from '@om-fe/api';
import { RpcStateStatus, useRpcState } from '@om-fe/widgets/dist/common/rpc/rpc-state.hooks';
import { Spinner } from '@om-fe/widgets/dist/components/spinner/spinner';

import { MatchListCommon, FilteredMarketGroupsOverview } from '../../../match-list/utils/match-list-models';
import { MatchListItemLayout } from '../../../match-list/utils/match-list.constants';
import { TopBetsMatchListPublicProps } from './top-bets-list';
import { TimeRangeType } from '../../utils/top-bets.constants';
import TopBetsMatchListSelectorMarketGroup from './top-bets-list-market-groups';
import { MatchListAddMoreAnchor } from '../../../match-list/match-list';

export interface TopBetsMatchListSelectorPublicProps extends MatchListCommon {
    readonly instanceId: string;
    readonly disciplineId: string;
    readonly redirectAnchor?: MatchListAddMoreAnchor;
    readonly itemLayout?: MatchListItemLayout;
    readonly liveStatus: DisciplineLiveStatus;
    readonly marketsHeaderOptions: MarketsHeaderOptions;
    readonly marketGroupsOverviewNumber?: number;
    readonly hasMarketSelector: boolean;
    readonly showMatchListHeader: boolean;
    readonly showMarketInMatchListGroupHeader: boolean;
    readonly deviceType?: PlatformTypes;
    readonly showMarketSelectorHeader?: boolean;
    readonly timeRange: string;
    readonly locationId?: string;
    readonly categoryId?: string;
    readonly isTopBets?: boolean;
    readonly isCategory?: boolean;
    readonly timeRangeType?: TimeRangeType;
    readonly timeRangeValue?: string;
}

type TopBetsMatchListSelectorProps = TopBetsMatchListSelectorPublicProps
    & TopBetsMatchListPublicProps
    & I18nContextInterface
    & EMRoutingContextInterface;

function TopBetsMatchListSelector(
    {
        instanceId,
        disciplineId,
        liveStatus,
        limitNumberOfEvents,
        mainMarketsLimit,
        marketsHeaderOptions,
        hasMarketSelector,
        showMarketSelectorHeader,
        showMatchListHeader,
        showMarketInMatchListGroupHeader,
        deviceType,
        itemLayout,
        timeRange,
        locationId,
        categoryId,
        isTopBets,
        isCategory,
        timeRangeType,
        timeRangeValue,
    }: TopBetsMatchListSelectorProps,
) {
    const params: GetMarketGroupsOptions = useMemo(() => ({
        sportId: disciplineId === DisciplinesType.ElectronicSports && locationId ? locationId : disciplineId,
        liveStatus,
    }), [disciplineId, liveStatus, locationId]);
    const [rpcResponse] = useRpcState<GetMarketGroupsResponse>('marketGroupsOverview', params);

    if (
        rpcResponse.status === RpcStateStatus.Loading || 
        rpcResponse.status === RpcStateStatus.Initial || 
        rpcResponse.status === RpcStateStatus.Failed
    ) {
        return <Spinner/>;
    }

    const { data } = rpcResponse;
    const marketGroupsOverviewData: MarketGroupOverviewModel[] | undefined = data.response;
    const filteredMarketGroupsOverviewData: Record<string, FilteredMarketGroupsOverview> = keyBy(marketGroupsOverviewData, 'id');
    const filteredMarketGroupsOverviewListData = marketGroupsOverviewData?.map(item => item.id);
    const groupOverviewData = filteredMarketGroupsOverviewListData?.slice(0, mainMarketsLimit).join(',');

    return <TopBetsMatchListSelectorMarketGroup
        key={`${instanceId}-${mainMarketsLimit}-${groupOverviewData || ''}`}
        disciplineId={disciplineId}
        instanceId={instanceId}
        liveStatus={liveStatus}
        limitNumberOfEvents={limitNumberOfEvents}
        marketsHeaderOptions={marketsHeaderOptions}
        mainMarketsLimit={mainMarketsLimit}
        groupOverviewData={groupOverviewData}
        hasMarketSelector={filteredMarketGroupsOverviewListData?.length ? hasMarketSelector : false}
        itemLayout={itemLayout}
        filteredMarketGroupsOverview={filteredMarketGroupsOverviewData}
        filteredMarketGroupsOverviewList={filteredMarketGroupsOverviewListData}
        deviceType={deviceType}
        showMarketSelectorHeader={showMarketSelectorHeader}
        showMatchListHeader={locationId === '0' ? false : showMatchListHeader}
        showMarketInMatchListGroupHeader={showMarketInMatchListGroupHeader}
        timeRange={timeRange}
        isTopBets={isTopBets}
        locationId={locationId}
        categoryId={categoryId}
        isCategory={isCategory}
        timeRangeType={timeRangeType}
        timeRangeValue={timeRangeValue}
    />
}

export default TopBetsMatchListSelector;